import { PreviewHousehold } from "@/schema"
import { ButtonBadge } from "@ui/button-badge"
import { Badge } from "@ui/shad"
import { ExternalLink, Tag, User, WandSparkles } from "lucide-react"

import { usePeekViewData } from "@/hooks/api/peek-views/queries"
import { Link } from "@/components/book-link"
import { PhoneBadgeWithRingout } from "@/components/tables/phone-badge-with-ringout"

import PeekViewSkeleton from "./skeleton"

export default function HouseholdPeekView({ householdId, href }: { householdId: string; href: string | null }) {
  const { data: household, isLoading } = usePeekViewData(householdId, "Household") as {
    data: PreviewHousehold
    isLoading: boolean
  }

  if (isLoading) return <PeekViewSkeleton />

  return (
    <div className="flex w-xs flex-col items-stretch gap-2 xl:w-sm">
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-baseline">
          <p className="truncate font-medium">{household.name}</p>
          {href && (
            <Link
              href={href}
              className="text-muted-foreground hover:text-link ml-2 transition-colors"
              onClick={(e) => e.stopPropagation()}
              aria-label="Open in new page"
            >
              <ExternalLink className="size-3.5" strokeWidth={1.5} />
            </Link>
          )}
        </div>
        <Badge variant="outline" className="flex-none">
          {household.type}
        </Badge>
      </div>
      <div className="flex items-center gap-2">
        <User className="text-icon size-4 flex-none" />
        {household.email && (
          <ButtonBadge text={household.email || ""} copyText={household.email || ""} className="max-w-1/2 truncate" />
        )}
        {household.phone_number && (
          <PhoneBadgeWithRingout
            value={household.phone_number}
            extension={household.phone_number_extension}
            name={household.name}
          />
        )}
      </div>
      <div className="flex items-center gap-2 truncate">
        <Tag className="text-icon size-4 flex-none" />

        {household.tags.slice(0, 3).map((tag) => (
          <Badge key={tag.id} variant="secondary" className="flex-none">
            {tag.name}
          </Badge>
        ))}
        {household.tags.length > 3 && (
          <Badge variant="secondary" className="flex-none">
            +{household.tags.length - 3}
          </Badge>
        )}
      </div>
      <div className="flex items-start gap-2">
        <WandSparkles className="text-icon mt-1 size-4 flex-none" />
        <p className="w-full text-sm">{household.ai_summary}</p>
      </div>
    </div>
  )
}
